import React, { useCallback } from 'react';
import { Category } from '@mui/icons-material';
import './SetTooltip.css';
import StatsList from '../StatsList';
import Tooltip from '../utils/Tooltip';
import MediaImg from '../utils/MediaImg';
import { processSetBonuses } from '../../core/statProcessing';

const TXT_SET = { fr: 'Panoplie', en: 'Set', de: 'Set', es: 'Set', pt: 'Conjunto' };

const SetTooltip = React.forwardRef(({
    set,
    lang = 'fr',
    interactive = false,
    onOpenSet = null,
    onOpenItem = null,
    onDismiss = null,
    onMouseLeave = null,
}, ref) => {
    // The hoverable variant is one big button: a click anywhere but a piece
    // opens the set, the same overlay a click on the tile opens. The opener
    // comes from the caller — the search has a slot to equip into and the
    // blacklist picker replaces equipping altogether, and this knows neither.
    const handleTooltipClick = useCallback(() => {
        if (!interactive) return;
        onDismiss?.();
        onOpenSet?.();
    }, [interactive, onDismiss, onOpenSet]);

    const handlePieceClick = useCallback((event, item) => {
        event.stopPropagation();
        if (!item) return;
        onDismiss?.();
        onOpenItem?.(item);
    }, [onDismiss, onOpenItem]);

    if (!set) return null;

    // Process set bonuses - use same logic as SetCard: only show highest active tier
    // Format: [{ num_items_required: 2, effects: [...] }, { num_items_required: 3, effects: [...] }, ...]
    let bonuses = [];
    const allBonuses = set.bonuses || [];
    const pieces = set.pieces || 0;

    // A set that came out of a build carries a piece count, and shows the tier
    // that count has reached — nothing below two pieces, because nothing is
    // active yet. A set out of the search catalog carries no count at all: it is
    // not equipped anywhere, so it shows what the full set is worth, the same
    // tier its result card opens on. The count is what tells the two apart, and
    // is only worth printing when there is one.
    const isEquippedSet = Number.isFinite(set.pieces);
    const selectedTierIndex = isEquippedSet
        ? (pieces >= 2 ? Math.min(pieces - 2, allBonuses.length - 1) : -1)
        : allBonuses.length - 1;

    if (selectedTierIndex >= 0) {
        const selectedTier = allBonuses[selectedTierIndex];
        // Ceilings this tier puts on a characteristic — inside the tier in the
        // raw/catalog shape, in a parallel list in the set-detail one. Only Cire
        // Momore has any; see backend core/set_caps.py.
        const tierCaps = selectedTier?.caps || set?.bonus_caps?.[selectedTierIndex] || [];
        if (selectedTier) {
            // Handle new format with effects array
            if (selectedTier.effects && Array.isArray(selectedTier.effects)) {
                // Transform effects to format expected by processSetBonuses
                const transformedEffects = selectedTier.effects.map(effect => ({
                    characteristic: effect.characteristic,
                    value: effect.value !== undefined ? effect.value : (effect.min !== undefined ? effect.min : (effect.max !== undefined ? effect.max : 0))
                }));
                // processSetBonuses expects an array containing an array of effects
                bonuses = processSetBonuses([transformedEffects], lang, tierCaps);
            }
            // Handle old format (array of effects directly)
            else if (Array.isArray(selectedTier)) {
                const transformedEffects = selectedTier.map(effect => ({
                    characteristic: effect.characteristic,
                    value: effect.value !== undefined ? effect.value : (effect.min !== undefined ? effect.min : (effect.max !== undefined ? effect.max : 0))
                }));
                bonuses = processSetBonuses([transformedEffects], lang, tierCaps);
            }
        }
    }

    // Fallback: bonuses that are neither shape (a flat { id, value } list) still
    // read straight.
    if (bonuses.length === 0 && allBonuses.length > 0) {
        bonuses = processSetBonuses(allBonuses, lang);
    }

    // Get active and inactive items from the new server structure
    // Handle both new format (active_items/inactive_items) and old format (items array with isActive property)
    let activeItems = set.active_items || [];
    let inactiveItems = set.inactive_items || [];

    // If active_items/inactive_items are not provided, try to split items array by isActive property
    if (activeItems.length === 0 && inactiveItems.length === 0 && set.items && Array.isArray(set.items)) {
        activeItems = set.items.filter(item => item && item.isActive !== false);
        inactiveItems = set.items.filter(item => item && item.isActive === false);
    }

    // Helper to safely get item name
    const getItemName = (item) => {
        if (!item) return 'Item';
        if (item.name && typeof item.name === 'object') {
            return item.name[lang] || item.name.fr || item.name.en || 'Item';
        }
        return item.name || 'Item';
    };

    // The level the set is worth, the same one its result card prints: the
    // highest its pieces reach. Reduced rather than Math.max'd over a filtered
    // list, which returns -Infinity for a set whose pieces carry no level.
    const maxLevel = (set.items || []).reduce((top, item) => Math.max(top, item?.level || 0), 0);

    // Helper to safely get item icon
    const getItemIcon = (item) => {
        return item?.icon || '/img/stat_re_armes.png';
    };

    // A piece, as a thumbnail under its own name tooltip — and as a button where
    // the caller has an overlay to open for it. The panel names the pieces
    // nowhere else, so the thumbnail is the only handle on one.
    const renderPiece = (item, key, state) => {
        const name = getItemName(item);
        const icon = (
            <MediaImg
                src={getItemIcon(item)}
                alt={name}
                retry={Boolean(item?.icon)}
            />
        );
        return (
            <div key={key} className={`set-tooltip-item ${state}`}>
                <Tooltip content={name}>
                    {onOpenItem ? (
                        <button
                            type="button"
                            className="set-tooltip-item-button"
                            aria-label={name}
                            onClick={(e) => handlePieceClick(e, item)}
                        >
                            {icon}
                        </button>
                    ) : icon}
                </Tooltip>
            </div>
        );
    };

    return (
        <div
            ref={ref}
            className={`set-tooltip${interactive ? ' set-tooltip--interactive' : ''}`}
            onClick={handleTooltipClick}
            onMouseLeave={onMouseLeave || undefined}
        >
            <div className="set-tooltip-header column">
                <div className="set-tooltip-info row">
                    <h6 className="set-tooltip-type">
                        <Category className="set-tooltip-type-icon" />
                        <span className="set-tooltip-type-text">{TXT_SET[lang] || TXT_SET.en}</span>
                    </h6>
                    {maxLevel > 0 && (
                        <span className="set-tooltip-info-dot" aria-hidden="true">•</span>
                    )}
                    {maxLevel > 0 && (
                        <h6 className="set-tooltip-level">
                            <span className="set-tooltip-level-value">{maxLevel}</span>
                        </h6>
                    )}
                </div>
                <h4 className="set-tooltip-name">{set.name && typeof set.name === 'object' ? set.name[lang] : set.name}</h4>
            </div>

            <div className="set-tooltip-items ">
                <div className="set-tooltip-items-list row">
                    {isEquippedSet && (
                        <div className="set-tooltip-pieces">{set.pieces}<span className="set-tooltip-pieces-span">/{set.max_pieces}</span></div>
                    )}
                    {activeItems.map((item, index) => renderPiece(item, `active-${index}`, 'active'))}
                    {inactiveItems.map((item, index) => renderPiece(item, `inactive-${index}`, 'inactive'))}
                </div>
            </div>

            {bonuses.length > 0 && (
                <div className="set-tooltip-bonuses">
                    <StatsList
                        stats={bonuses}
                        className="set-tooltip-bonuses-list"
                        lang={lang}
                    />
                </div>
            )}
        </div>
    );
});

export default SetTooltip; 
